import React, { Fragment, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { investorApi, ownerApi } from '../../api/portal';
import usePortalQuery from '../../hooks/usePortalQuery';
import { LineItems, QueryStatus, StatementLines, Thumbnail, Totals, Trend } from '../../components/portal/PortalUI';
import Tabs, { useTabParam } from '../../components/portal/Tabs';
import VehicleEarnings from '../../components/portal/VehicleEarnings';
import { carName, currentMonth, exportCsv, lineTotals, money, totalsDiffer, monthIndex, previousMonth, shiftMonth, validRange } from './finance';

function useOwnerScope() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const admin = user.role === 'admin';
  const scope = admin ? params.get('investor_id') || '' : user.investor_id;
  const period = params.get('period') || previousMonth();
  const validMonth = Number.isFinite(monthIndex(period));
  const enabled = validMonth && !!scope;
  const scoped = admin && scope !== 'all' ? { investor_id: scope } : {};
  const list = usePortalQuery(['preview-investors'], (signal) => investorApi.list('', signal), admin);
  const change = (key, value) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next);
  };
  const suffix = `?${params.toString() || `period=${period}`}`;
  return { user, admin, scope, period, validMonth, enabled, scoped, list, change, params, suffix };
}

const OwnerHeader = ({ scope, title, month = true }) => <header className="portal-header">
  <h1>{title}</h1><p className="portal-muted">Financial statements | USD | Reporting, not investor payouts.</p>
  <nav aria-label="Investor portal">
    <Link to={`/owner${scope.suffix}`}>Overview</Link>
    <Link to={`/owner/cars${scope.suffix}`}>My Cars</Link>
    <Link to={`/owner/reports${scope.suffix}`}>Reports</Link>
    {!scope.admin && <Link to="/owner/profile">Account</Link>}
  </nav>
  <div className="portal-controls">
    {scope.admin && <label>Preview investor<select value={scope.scope} onChange={(event) => scope.change('investor_id', event.target.value)}>
      <option value="">Select an investor</option><option value="all">All investors</option>
      {scope.list.data?.map((investor) => <option value={investor.id} key={investor.id}>{investor.name}</option>)}
    </select></label>}
    {month && <label>Statement month<input type="month" value={scope.period} onChange={(event) => scope.change('period', event.target.value)} /></label>}
  </div>
  {scope.admin && <><QueryStatus query={scope.list} /><p className="portal-warning">Admin preview: {scope.scope === 'all' ? 'All investors (whole fleet)' : scope.scope ? 'Selected investor only' : 'Select an investor before loading financial data.'}</p></>}
  {!scope.validMonth && <p role="alert" className="portal-error">Select a valid statement month.</p>}
</header>;

export default function OwnerPortal() {
  const scope = useOwnerScope();
  const { vehicleId } = useParams();
  const location = useLocation();
  if (location.pathname === '/owner/profile') return <OwnerProfile scope={scope} />;
  if (location.pathname === '/owner/reports') return <OwnerReports scope={scope} />;
  if (location.pathname === '/owner/cars') return <OwnerCars scope={scope} />;
  if (location.pathname.startsWith('/owner/cars/') && vehicleId) return <OwnerCarDetail scope={scope} vehicleId={vehicleId} />;
  if (vehicleId) return <OwnerVehicle scope={scope} vehicleId={vehicleId} />;
  return <OwnerOverview scope={scope} />;
}

function OwnerOverview({ scope }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('net');
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (vehicleId) => setExpanded((current) => {
    const next = new Set(current);
    if (!next.delete(vehicleId)) next.add(vehicleId);
    return next;
  });
  const query = usePortalQuery(['owner-overview', scope.scope, scope.period], async (signal) => {
    const [overview, summary] = await Promise.all([
      ownerApi.overview({ period: scope.period, ...scope.scoped }, signal),
      ownerApi.summary({ from: shiftMonth(scope.period, -5), to: scope.period, ...scope.scoped }, signal),
    ]);
    return { overview, summary };
  }, scope.enabled);
  const overview = query.data?.overview;
  const vehicles = overview?.vehicles.filter((car) =>
    `${carName(car)} ${car.plate_number || ''} ${car.vin || ''} ${car.vehicle_id}`.toLowerCase().includes(search.toLowerCase()))
    .slice().sort((a, b) => sort === 'plate_number' ? (a.plate_number || a.vehicle_id).localeCompare(b.plate_number || b.vehicle_id) : b[sort] - a[sort]);
  return <main className="portal portal-investor">
    <OwnerHeader scope={scope} title={overview?.investor?.name ? `${overview.investor.name}'s financial statements` : 'Financial statements'} />
    <QueryStatus query={query} />
    {overview && <>
      {overview.totals.vehicle_count > 0 && <Totals value={overview.totals} />}
      <p>Cars with statements: <strong>{overview.totals.vehicle_count}</strong></p>
      <section className="portal-card"><h2>Portfolio statements</h2>
        <div className="portal-controls"><label>Search cars<input value={search} onChange={(event) => setSearch(event.target.value)} /></label><label>Sort by<select value={sort} onChange={(event) => setSort(event.target.value)}><option value="net">Net income (highest first)</option><option value="total_earnings">Earnings (highest first)</option><option value="total_cost">Costs (highest first)</option><option value="plate_number">Plate</option></select></label></div>
        {!overview.vehicles.length ? <p>No statement available for {scope.period}. Missing statements are not zero earnings.</p> :
          !vehicles.length ? <p>No cars match your search.</p> : <div className="portal-table-scroll"><table className="portal-portfolio"><caption>Cars with statements for {scope.period} (USD)</caption><thead><tr><th>Car</th><th>Plate</th><th>Earnings</th><th>Costs</th><th>Net income</th><th>Details</th></tr></thead><tbody>{vehicles.map((car) => {
            const open = expanded.has(car.vehicle_id);
            const panelId = `statement-${car.vehicle_id}`;
            return <Fragment key={car.vehicle_id}>
              <tr className={`portal-expandable-row${open ? ' portal-expanded' : ''}`} onClick={() => toggle(car.vehicle_id)}>
                <td className="portal-car-cell"><div className="portal-car">{car.thumbnail_url && <Thumbnail url={car.thumbnail_url} label={carName(car)} />}<div><div>{carName(car)}</div>{car.vin && <small>VIN: {car.vin}</small>}</div></div></td>
                <td className="portal-plate-cell">{car.plate_number || car.vehicle_id}</td><td data-label="Earnings">{money(car.total_earnings)}</td><td data-label="Costs">{money(car.total_cost)}</td><td data-label="Net">{money(car.net)}</td>
                <td className="portal-toggle-cell"><button type="button" className="portal-link-button" aria-expanded={open} aria-controls={panelId} onClick={(event) => { event.stopPropagation(); toggle(car.vehicle_id); }}>
                  {open ? 'Hide statement' : 'View statement'}<span className="sr-only"> for {car.plate_number || car.vehicle_id}</span>
                </button></td>
              </tr>
              {open && <tr className="portal-detail-row"><td colSpan={6} id={panelId}><VehicleStatement scope={scope} vehicleId={car.vehicle_id} /></td></tr>}
            </Fragment>;
          })}</tbody></table></div>}
      </section>
      <Trend rows={query.data.summary.trend} />
    </>}
  </main>;
}

function OwnerVehicle({ scope, vehicleId }) {
  return <main className="portal portal-investor">
    <OwnerHeader scope={scope} title="Vehicle financial statement" />
    <Link to={`/owner${scope.suffix}`}>Back to portfolio</Link>
    <VehicleStatement scope={scope} vehicleId={vehicleId} showSummary />
  </main>;
}

function VehicleStatement({ scope, vehicleId, showSummary = false }) {
  const query = usePortalQuery(['vehicle-statements', scope.scope, vehicleId, scope.period],
    (signal) => ownerApi.detail(vehicleId, { period: scope.period, ...scope.scoped }, signal), scope.enabled);
  const detail = query.data;
  const car = detail?.vehicle || detail?.statements[0] || { vehicle_id: vehicleId };
  const calculated = detail && lineTotals(detail.statements);
  const currency = detail?.statements[0]?.currency || 'USD';
  return <>
    <QueryStatus query={query} />
    {detail && (detail.statements.length ? <>
      {showSummary && <section className="portal-card portal-car-summary">{detail.thumbnail_url && <Thumbnail url={detail.thumbnail_url} label={carName(car)} />}<div><h2>{carName(car)}</h2><p>{car.plate_number || detail.statements[0].plate_number || vehicleId}{car.vin && ` | VIN: ${car.vin}`}</p></div></section>}
      {totalsDiffer(calculated, detail.totals) && <p className="portal-warning">Line items do not match the statement totals ({money(detail.totals.total_earnings, currency)} earnings, {money(detail.totals.total_cost, currency)} costs).</p>}
      {showSummary && <Totals value={calculated} currency={currency} />}
      <div className="portal-lines-grid">
        <StatementLines kind="earning" items={detail.statements.flatMap((statement) => statement.earnings)} currency={currency} />
        <StatementLines kind="cost" items={detail.statements.flatMap((statement) => statement.costs)} currency={currency} />
      </div>
      {!showSummary && <p className="portal-net"><span>Net</span><strong>{money(calculated.net, currency)}</strong></p>}
    </> : <p>No statement for this car in {scope.period}.</p>)}
  </>;
}

function OwnerReports({ scope }) {
  const from = scope.params.get('from') || shiftMonth(scope.period, -5);
  const to = scope.params.get('to') || scope.period;
  const [search, setSearch] = useState('');
  const valid = validRange(from, to);
  const query = usePortalQuery(['owner-reports', scope.scope, from, to], async (signal) => {
    const [lines, summary] = await Promise.all([
      ownerApi.lines({ from, to, ...scope.scoped }, signal),
      ownerApi.summary({ from, to, ...scope.scoped }, signal),
    ]);
    return { lines, summary };
  }, scope.enabled && valid);
  const download = () => {
    const blob = new Blob(['\uFEFF', exportCsv(query.data.lines.line_items, from, to)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `financial-statements-${from}-${to}.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <main className="portal portal-investor">
    <OwnerHeader scope={scope} title="Financial reports" month={false} />
    <div className="portal-controls"><label>From month<input type="month" value={from} onChange={(event) => scope.change('from', event.target.value)} /></label><label>To month<input type="month" value={to} onChange={(event) => scope.change('to', event.target.value)} /></label></div>
    {!valid && <p role="alert" className="portal-error">Choose both months, with the end not before the start, spanning at most 12 months.</p>}
    <QueryStatus query={query} />
    {query.data && <>
      <Totals value={query.data.lines.totals} /><Trend rows={query.data.summary.trend} />
      <div className="portal-controls"><label>Search line items<input value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <button onClick={download}>Export all {query.data.lines.count} line items (CSV)</button></div>
      <p className="portal-muted">Export includes every line item in the selected period and scope, regardless of the table search. Currency: USD.</p>
      {query.data.lines.line_items.some((item) => item.source === 'investor_report') && <p className="portal-muted">Investor report entries are itemized income and cost lines from the workbook details. No management fee is recalculated.</p>}
      <LineItems report items={query.data.lines.line_items.filter((item) => `${item.description} ${item.category || ''} ${item.vehicle_name || ''} ${item.vin || ''} ${item.plate_number || ''} ${item.vehicle_id}`.toLowerCase().includes(search.toLowerCase()))} title="Report entries" />
    </>}
  </main>;
}

function OwnerProfile({ scope }) {
  const query = usePortalQuery(['investor-profile', scope.user.investor_id], (signal) => investorApi.get(scope.user.investor_id, signal), !scope.admin);
  return <main className="portal portal-investor"><OwnerHeader scope={scope} title="My account" month={false} /><QueryStatus query={query} />
    {query.data && <section className="portal-card"><dl>{[['name', 'Name'], ['email', 'Gmail address'], ['phone', 'Phone'], ['status', 'Status']].map(([field, label]) => <React.Fragment key={field}><dt>{label}</dt><dd>{query.data[field] || 'Not provided'}</dd></React.Fragment>)}</dl><p className="portal-muted">Sign in with Google using this Gmail address. Contact an administrator to update your account.</p></section>}
  </main>;
}

const carsInvestorId = (scope) => scope.admin ? (scope.scope && scope.scope !== 'all' ? scope.scope : '') : scope.user.investor_id;
const carSuffix = (scope) => {
  const next = new URLSearchParams();
  if (scope.admin && scope.scope) next.set('investor_id', scope.scope);
  return next.toString() ? `?${next.toString()}` : '';
};

function OwnerCars({ scope }) {
  const investorId = carsInvestorId(scope);
  const month = currentMonth();
  const [search, setSearch] = useState('');
  const query = usePortalQuery(['owner-cars', investorId, month],
    (signal) => ownerApi.vehicles({ period: month, ...(scope.admin ? { investor_id: investorId } : {}) }, signal), !!investorId);
  const all = query.data?.vehicles || [];
  const cars = all.filter((car) =>
    `${carName(car)} ${car.plate_number || ''} ${car.vin || ''} ${car.id}`.toLowerCase().includes(search.toLowerCase()));
  return <main className="portal portal-investor">
    <OwnerHeader scope={scope} title={query.data?.investor?.name ? `${query.data.investor.name}'s cars` : 'My cars'} month={false} />
    {!investorId ? <p>Select a specific investor to preview their cars.</p> : <>
      <QueryStatus query={query} />
      {query.data && <section className="portal-card"><h2>Owned cars</h2>
        {!all.length ? <p>No cars are currently assigned to this account.</p> : <>
          <div className="portal-controls"><label>Search cars<input value={search} onChange={(event) => setSearch(event.target.value)} /></label></div>
          {!cars.length ? <p>No cars match your search.</p> : <div className="portal-table-scroll"><table className="portal-portfolio">
            <caption>{query.data.count} owned cars ({query.data.period}, USD)</caption>
            <thead><tr><th>Car</th><th>Plate</th><th>Earnings</th><th>Costs</th><th>Net income</th><th>Details</th></tr></thead>
            <tbody>{cars.map((car) => <tr key={car.id}>
              <td className="portal-car-cell"><div className="portal-car"><Thumbnail url={car.thumbnail_url} label={carName(car)} />
                <div><div>{carName(car)}</div>{car.year && <small>{car.year}</small>}</div></div></td>
              <td className="portal-plate-cell">{car.plate_number || car.id}</td>
              <td data-label="Earnings">{money(car.totals.total_earnings)}</td>
              <td data-label="Costs">{money(car.totals.total_cost)}</td>
              <td data-label="Net">{car.has_statement ? money(car.totals.net) : 'No activity'}</td>
              <td><Link to={`/owner/cars/${encodeURIComponent(car.id)}${carSuffix(scope)}`}>View car<span className="sr-only"> {car.plate_number || car.id}</span></Link></td>
            </tr>)}</tbody></table></div>}
        </>}
      </section>}
    </>}
  </main>;
}

function OwnerCarDetail({ scope, vehicleId }) {
  const [tab, setTab] = useTabParam(['profile', 'earnings']);
  const investorId = carsInvestorId(scope);
  const filters = scope.admin && investorId ? { investor_id: investorId } : {};
  const query = usePortalQuery(['owner-car', vehicleId, investorId],
    (signal) => ownerApi.vehicle(vehicleId, filters, signal), !!investorId);
  const notOwned = query.error?.status === 404;
  const profile = query.data?.profile;
  return <main className="portal portal-investor">
    <OwnerHeader scope={scope} title={profile ? carName(profile) : 'Car details'} month={false} />
    <p><Link to={`/owner/cars${carSuffix(scope)}`}>← My cars</Link></p>
    {!investorId ? <p>Select a specific investor to preview their cars.</p> :
      notOwned ? <section className="portal-card"><p>This car was not found or is not currently owned by this account.</p></section> : <>
        <QueryStatus query={query} />
        {profile && <Tabs label="Car sections" value={tab} onChange={setTab} tabs={[
          { id: 'profile', label: 'Car Profile', content: <OwnerCarProfile profile={profile} thumbnailUrl={query.data.thumbnail_url} /> },
          { id: 'earnings', label: 'Earnings & Costs', content: <VehicleEarnings vehicleId={profile.id} filters={filters} /> },
        ]} />}
      </>}
  </main>;
}

function OwnerCarProfile({ profile, thumbnailUrl }) {
  const field = (value) => value || 'Not provided';
  return <section className="portal-card">
    <div className="portal-car-summary"><Thumbnail url={thumbnailUrl} label={carName(profile)} />
      <div><h2>{carName(profile)}</h2><p>{profile.plate_number || profile.id}</p></div></div>
    <dl>
      <dt>Plate number</dt><dd>{field(profile.plate_number)}</dd>
      <dt>Make / model</dt><dd>{field([profile.Make, profile.Model].filter(Boolean).join(' '))}</dd>
      <dt>Year</dt><dd>{field(profile.year)}</dd>
      <dt>Description</dt><dd>{field(profile.description)}</dd>
      <dt>VIN</dt><dd>{field(profile.vin)}</dd>
      <dt>Profile ID</dt><dd>{profile.id}</dd>
    </dl>
  </section>;
}
